import { Badge } from "@/components/Badge";
import { HStack, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { TextVariant } from "@/components/Typography/Text.variant";
import { Separator } from "@/components/ui/separator";
import { useFormatDate } from "@/hooks/date";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { useGetCycleUnitText } from "@/hooks/useGetCycleUnitText";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { formatCurrency } from "@/utils/currency";
import { match } from "ts-pattern";
import type { AppContractRes } from "../_$id/-query/type";

export default function AppIntegrationContractSummary({
  contract,
}: {
  contract: AppContractRes["appContract"];
}) {
  const { i18n, t } = useTranslation(defaultNS, {
    keyPrefix: "pages.profile.app.header",
  });
  const tc = useCommonTranslation();
  const getCycleUnitText = useGetCycleUnitText();

  const formatDate = useFormatDate();

  const contractAnchorMonth =
    contract.contractAnchorMonth &&
    formatDate(new Date(0, contract.contractAnchorMonth - 1), "MMMM");

  const contractAnchorDay =
    contract.contractAnchorDay &&
    formatDate(new Date(0, 0, contract.contractAnchorDay), "Do");

  const contractRenewalDateText = match(contract.billingCycleUnit)
    .with("MONTH", () =>
      contractAnchorDay
        ? t("contractRenewalCycleMonthly", { day: contractAnchorDay })
        : tc("enDash"),
    )
    .with("YEAR", () =>
      contractAnchorMonth && contractAnchorDay
        ? t("contractRenewalCycleYearly", {
            month: contractAnchorMonth,
            day: contractAnchorDay,
          })
        : tc("enDash"),
    )
    .otherwise(() => tc("enDash"));

  const currencyDisplay = match(contract.pricingModel)
    .with("FREE", () => t("currencyDisplayFree"))
    .with("LINEAR", "FIXED", () =>
      contract.priceOfUnitPerBillingCycle && contract.currencyCode
        ? formatCurrency({
            amount: contract.priceOfUnitPerBillingCycle,
            currencyCode: contract.currencyCode,
            locale: i18n.language,
          })
        : tc("enDash"),
    )
    .otherwise(() => t("currencyDisplayUnpredictable"));

  const billingCycle = match(contract.pricingModel)
    .with("LINEAR", "FIXED", () =>
      contract.billingCycleUnit
        ? getCycleUnitText(
            contract.billingCycleUnit,
            contract.billingCycleLength ?? 1,
          )
        : "",
    )
    .otherwise(() => "");

  const unitText = match(contract.pricingModel)
    .with("FIXED", () => t("fixedUnit", { cycle: billingCycle }))
    .with("LINEAR", () => {
      const unitMap = {
        BILLABLE_USER: "billableUserUnit",
        LICENSE_SEAT: "licenseSeatUnit",
      } as const;
      return contract.unit
        ? t(unitMap[contract.unit], { cycle: billingCycle })
        : "";
    })
    .otherwise(() => "");
  return (
    <HStack className="ml-auto items-center gap-8">
      <VStack className="gap-2">
        <HStack className="gap-2">
          <Text variant={TextVariant.pSmall}>{t("contractRenewalDate")}</Text>
        </HStack>
        <Text variant={TextVariant.labelLarge}>{contractRenewalDateText}</Text>
      </VStack>
      <Separator orientation="vertical" />
      <VStack className="gap-2">
        <HStack className="items-center gap-2">
          <Text variant={TextVariant.p}>{t("plan")}</Text>
          {contract.planName && (
            <Badge variant="secondary">{contract.planName}</Badge>
          )}
        </HStack>
        <HStack className="items-baseline">
          <Text variant={TextVariant.labelLarge}>{currencyDisplay}</Text>
          <Text variant={TextVariant.label} className="ml-2.5">
            {unitText}
          </Text>
        </HStack>
      </VStack>
    </HStack>
  );
}
